import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import { UniversalJsonRenderer } from '../components/UniversalJsonRenderer';
import FileUploader, { UploadedFile } from '../components/FileUploader';
import ChatInterface, { ChatMessage, CitationClick, QuoteCitation } from '../components/ChatInterfaceQuoteUpdate';
import PdfViewer, { CitationHighlight } from '../components/PdfViewer';
import HtmlPdfViewer from '../components/HtmlPdfViewer';
import { UnifiedPdfCitationViewer } from '../components/UnifiedPdfCitationViewer';
import { useDocuments } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { aiService } from '../services/aiService';
import { spansToPrompt } from '../utils/spansToPrompt';
import html2pdf from 'html2pdf.js';

// Legacy citation service removed
interface ParsedCitation {
  id?: string;
  quote?: string;
  text?: string;
  documentId: string;
  docId?: string;
  page?: number;
  startOffset?: number;
  endOffset?: number;
  documentName?: string;
  context?: string;
}

type WizardStep = 'customer-selection' | 'document-selection' | 'report-details' | 'generating-report' | 'view-report';

interface ProgressStatus {
  message: string;
  progress: number;
}

interface Customer {
  id: string;
  name: string;
  type: 'customer' | 'prospect';
  email?: string;
  phone?: string;
  company?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const Comparison: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const {
    documents,
    addDocuments,
    selectedDocumentIds,
    toggleDocumentSelection,
    setSelectedDocumentIds,
    chatHistory,
    setChatHistory,
    setCurrentCustomer,
    setCurrentProject
  } = useDocuments();

  const [currentStep, setCurrentStep] = useState<WizardStep>('customer-selection');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerDocuments, setCustomerDocuments] = useState<any[]>([]);
  const [wizardUploadedDocs, setWizardUploadedDocs] = useState<UploadedFile[]>([]);
  const wizardUploadedDocsRef = useRef<UploadedFile[]>([]);
  const [reportTitle, setReportTitle] = useState('');
  const [additionalFacts, setAdditionalFacts] = useState('');
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>({ message: '', progress: 0 });
  const [comparisonReport, setComparisonReport] = useState<string>('');
  const [reportCitations, setReportCitations] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const reportContentRef = useRef<HTMLDivElement>(null);
  const [clickedCitation, setClickedCitation] = useState<CitationClick | null>(null);
  const [highlight, setHighlight] = useState<CitationHighlight | null>(null);
  
  // PDF viewer state for quote citations
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<ParsedCitation | null>(null);

  // HTML PDF viewer state for citations (legacy)
  const [showHtmlPdfViewer, setShowHtmlPdfViewer] = useState(false);
  const [htmlViewerCitation, setHtmlViewerCitation] = useState<ParsedCitation | null>(null);
  const [htmlViewerDocumentUrl, setHtmlViewerDocumentUrl] = useState<string>('');
  const [allCitationsOnPage, setAllCitationsOnPage] = useState<ParsedCitation[]>([]);

  // Initialize navigation based on selected documents from context
  useEffect(() => {
    // If we have selected documents, skip customer selection and go to document selection
    if (selectedDocumentIds.length > 0) {
      setCurrentStep('document-selection');
    }
  }, [selectedDocumentIds]);

  // FORCE: Watch the documents array and add ANY new document to wizard docs
  useEffect(() => {
    if (currentStep === 'document-selection' && documents.length > 0) {
      console.log('🔥 FORCING DOCUMENT TRACKING');
      console.log('🔥 Context documents:', documents.length);
      console.log('🔥 Current wizard docs:', wizardUploadedDocs.length);
      
      // Find documents that aren't in our wizard array yet
      const newDocs = documents.filter(doc => 
        !wizardUploadedDocs.some(wizDoc => wizDoc.id === doc.id) &&
        !customerDocuments.some(custDoc => custDoc.id === doc.id)
      );
      
      if (newDocs.length > 0) {
        console.log('🔥 FOUND NEW DOCUMENTS TO ADD:', newDocs.map(d => d.name));
        setWizardUploadedDocs(prev => [...prev, ...newDocs]);
        wizardUploadedDocsRef.current = [...wizardUploadedDocsRef.current, ...newDocs];
        
        // Auto-advance if we have enough
        setTimeout(() => {
          const totalDocs = customerDocuments.length + wizardUploadedDocsRef.current.length;
          if (totalDocs >= 2) {
            console.log('🔥 AUTO-ADVANCING WITH TOTAL DOCS:', totalDocs);
            setCurrentStep('report-details');
          }
        }, 1000);
      }
    }
  }, [documents, currentStep, wizardUploadedDocs, customerDocuments]);

  // Fetch customers on component mount and check for customer context
  useEffect(() => {
    fetchCustomers();
    
    // Check for customer context from Home page navigation
    const storedContext = localStorage.getItem('riskninja-comparison-customer-context');
    if (storedContext) {
      try {
        const context = JSON.parse(storedContext);
        const customer: Customer = {
          id: context.customerId,
          name: context.customerName,
          type: context.type,
          email: context.email,
          phone: context.phone,
          company: context.company
        };
        
        handleCustomerSelection(customer);
        setCurrentStep('document-selection');
        
        // Clear the stored context after using it
        localStorage.removeItem('riskninja-comparison-customer-context');
      } catch (error) {
        console.error('Error parsing customer context:', error);
      }
    }
  }, []);

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const allCustomers = await response.json();
        setCustomers(allCustomers);
      } else {
        console.error('Failed to fetch customers');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Fetch documents for selected customer
  const fetchCustomerDocuments = async (customerId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const docs = await response.json();
        setCustomerDocuments(docs);
        
        // Automatically select all customer documents
        if (docs.length > 0) {
          const customerDocIds = docs.map((doc: any) => doc.id);
          const newIds = customerDocIds.filter((id: string) => !selectedDocumentIds.includes(id));
          
          if (newIds.length > 0) {
            setSelectedDocumentIds(prev => {
              const combined = [...prev, ...newIds];
              console.log('📌 Auto-selected customer documents:', newIds);
              console.log('📌 Total selected documents:', combined.length);
              return combined;
            });
          }
        }
      } else {
        console.error('Failed to fetch customer documents');
      }
    } catch (error) {
      console.error('Error fetching customer documents:', error);
    }
  };

  // Update customer selection handler
  const handleCustomerSelection = async (customer: Customer) => {
    setSelectedCustomer(customer);
    // Generate default report title
    const defaultTitle = `Policy Comparison - ${customer.name} - ${new Date().toLocaleDateString()}`;
    setReportTitle(defaultTitle);
    
    // Set customer and project in document context
    setCurrentCustomer(customer.id);
    setCurrentProject(customer.id); // Using customer ID as project ID for now
    
    // Fetch documents for this customer
    await fetchCustomerDocuments(customer.id);
  };

  // Handle loading existing report from navigation state or URL
  useEffect(() => {
    console.log('🔄 Comparison component mounted/updated');
    
    const urlParams = new URLSearchParams(location.search);
    const reportId = urlParams.get('reportId');
    
    if (reportId) {
      console.log('📊 Found reportId in URL, loading existing report...');
      
      // Try navigation state first
      const state = location.state as any;
      if (state?.reportData?.isExistingReport) {
        console.log('📊 Loading from navigation state');
        loadExistingReport(state.reportData);
        return;
      }
      
      // Try sessionStorage as fallback
      const storedData = sessionStorage.getItem('comparison-report-data');
      if (storedData) {
        try {
          const reportData = JSON.parse(storedData);
          console.log('📊 Loading from sessionStorage');
          loadExistingReport(reportData);
          sessionStorage.removeItem('comparison-report-data'); // Clean up
          return;
        } catch (error) {
          console.error('Error parsing stored report data:', error);
        }
      }
      
      // Fallback: fetch the report from the API
      console.log('📊 No local data found, fetching report from API...');
      fetchReportFromAPI(reportId);
    }
  }, [location, setChatHistory]);

  const fetchReportFromAPI = async (reportId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison-reports/${reportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const report = await response.json();
        console.log('📊 Successfully fetched report from API:', report.title);
        
        const reportDataPayload = {
          report: report.content,
          reportTitle: report.title,
          documentIds: report.documentIds,
          documentNames: report.documentNames,
          primaryPolicyType: report.primaryPolicyType,
          additionalFacts: report.additionalFacts,
          projectId: report.projectId,
          customerId: report.customerId,
          isExistingReport: true
        };

        loadExistingReport(reportDataPayload);
      } else {
        console.error('Failed to fetch report from API:', await response.text());
        setError('Failed to load the comparison report. The report may have been deleted or you may not have permission to access it.');
      }
    } catch (error) {
      console.error('Error fetching report from API:', error);
      setError('Failed to load the comparison report. Please check your connection and try again.');
    }
  };

  const loadExistingReport = (reportData: any) => {
    console.log('📊 Loading existing report:', reportData.reportTitle);

    // Validate that we have the required data
    if (!reportData.report || !reportData.reportTitle) {
      console.error('❌ Invalid report data:', reportData);
      setError('Invalid report data. Please try selecting the report again.');
      return;
    }

    // Set document context so documents load for the chat "Items sent to AI"
    if (reportData.projectId) {
      setCurrentCustomer(reportData.customerId || reportData.projectId);
      setCurrentProject(reportData.projectId);
    }

    setComparisonReport(reportData.report || '');
    setSelectedDocumentIds(reportData.documentIds || []);
    setAdditionalFacts(reportData.additionalFacts || '');
    setCurrentStep('view-report');
    
    // Initialize chat with report context
    const initialMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      content: `I've loaded the comparison report "${reportData.reportTitle}". This report analyzes ${reportData.documentNames?.join(', ') || 'your selected documents'}. Feel free to ask me any questions about the findings or the original documents!`,
      sender: 'ai',
      timestamp: new Date()
    };

    setChatHistory([initialMessage]);
    console.log('✅ Report loaded successfully');
  };

  const handleDocumentSelection = (docId: string) => {
    toggleDocumentSelection(docId);
    
    // Check the updated selection for auto-advance
    const isCurrentlySelected = selectedDocumentIds.includes(docId);
    const newCount = isCurrentlySelected 
      ? selectedDocumentIds.length - 1  // Will be removed
      : selectedDocumentIds.length + 1; // Will be added
    
    // Auto-advance if we have at least 2 documents
    if (newCount >= 2) {
      console.log('✅ Auto-proceeding with document comparison');
      setTimeout(() => setCurrentStep('report-details'), 100); // Small delay for state update
    }
  };

  const handleFilesUploaded = (newFiles: UploadedFile[]) => {
    console.log('🚨🚨🚨 HANDLEFILESUPLOADED CALLED 🚨🚨🚨');
    console.log('📄 Files uploaded:', newFiles.length);
    console.log('📄 File details:', newFiles.map(f => ({ id: f.id, name: f.name, status: f.status })));
    console.log('📄 Current wizardUploadedDocs from ref:', wizardUploadedDocsRef.current.length);
    console.log('📄 Current wizardUploadedDocs from state:', wizardUploadedDocs.length);
    
    // Add ALL completed files
    const completedFiles = newFiles.filter(file => file.status === 'completed');
    console.log('📄 Completed files to add:', completedFiles.length);
    
    if (completedFiles.length > 0) {
      // Update ref immediately
      wizardUploadedDocsRef.current = [...wizardUploadedDocsRef.current, ...completedFiles];
      console.log('📄 Updated ref total:', wizardUploadedDocsRef.current.length);
      
      // Update state
      setWizardUploadedDocs([...wizardUploadedDocsRef.current]);
      
      console.log('📌 Added files to wizard docs, will be auto-selected');
      console.log('📌 All wizard docs now:', wizardUploadedDocsRef.current.map(d => d.name));
      
      // Check if we have enough documents for auto-advance
      setTimeout(() => {
        const totalDocs = customerDocuments.length + wizardUploadedDocsRef.current.length;
        console.log('📄 Total docs for auto-advance check:', totalDocs);
        if (totalDocs >= 2 && currentStep === 'document-selection') {
          console.log('✅ Auto-proceeding with document comparison after upload');
          setCurrentStep('report-details');
        }
      }, 500);
    } else {
      console.warn('⚠️ No completed files to add to wizard');
    }
  };

  const handleNextFromDocuments = () => {
    const totalDocs = customerDocuments.length + wizardUploadedDocs.length;
    if (totalDocs < 2) return;
    setCurrentStep('report-details');
  };

  const handleBackToDocuments = () => {
    setCurrentStep('document-selection');
  };

  const handleNextFromReportDetails = async () => {
    setCurrentStep('generating-report');
    setError(null);

    try {
      // Simulate progress updates
      setProgressStatus({ message: 'Analyzing documents...', progress: 25 });
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProgressStatus({ message: 'Comparing coverages...', progress: 50 });
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProgressStatus({ message: 'Generating recommendations...', progress: 75 });
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get ALL documents for comparison - customer docs + wizard uploaded docs
      const allDocuments = [...customerDocuments, ...wizardUploadedDocs];
      const selectedDocuments = allDocuments;
      
      // Import prompt mapping utilities
      const { determinePrimaryPolicyType } = await import('../utils/promptMapping');
      
      // Determine the primary policy type from selected documents
      const primaryPolicyType = determinePrimaryPolicyType(selectedDocuments);
      console.log('🔍 Comparison page - Primary policy type:', primaryPolicyType);
      
      // Create comparison report request with citation options
      const documentIds = [...customerDocuments.map(d => d.id), ...wizardUploadedDocs.map(d => d.id)];
      const reportRequest = {
        documentIds: documentIds,
        additionalFacts: additionalFacts,
        primaryPolicyType: primaryPolicyType,
        documents: selectedDocuments.map(doc => ({
          id: doc.id,
          name: doc.name,
          policyType: doc.policyType,
          extractedText: doc.extractedText || ''
        })),
        options: {
          smartCitations: true,
          enhanceCitations: true,
          validateCitations: true
        }
      };

      console.log('📊 Generating comparison report for documents:', selectedDocuments.map(d => d.name));

      setProgressStatus({ message: 'Finalizing report...', progress: 90 });

      // Generate the comparison report via backend API
      const response = await fetch(`${API_BASE_URL}/api/comparison/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportRequest)
      });

      if (!response.ok) {
        throw new Error('Failed to generate comparison report');
      }

      const result = await response.json();
      const reportContent = result.report || 'Comparison report generated successfully.';
      const citations = result.citations || [];
      const validationResults = result.validationResults || null;
      
      console.log(`📊 Received ${citations.length} citations from comparison API`);
      if (validationResults) {
        console.log('✅ Citation validation results:', validationResults);
      }

      setProgressStatus({ message: 'Saving report...', progress: 95 });

      // Save to backend
      const documentNames = selectedDocuments.map(doc => doc.name);
      const reportTitle = `Comparison Report - ${documentNames.join(' vs ')}`;

      try {
        const saveResponse = await fetch(`${API_BASE_URL}/api/comparison-reports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            title: reportTitle,
            content: reportContent,
            citations: citations,
            validationResults: validationResults,
            documentNames: documentNames,
            documentIds: selectedDocumentIds,
            primaryPolicyType: primaryPolicyType,
            additionalFacts: additionalFacts,
            customerId: selectedCustomer?.id || null
          })
        });

        if (saveResponse.ok) {
          const savedReport = await saveResponse.json();
          console.log('✅ Report saved successfully:', savedReport.id);
        } else {
          console.error('Failed to save report:', await saveResponse.text());
        }
      } catch (saveError) {
        console.error('Error saving report:', saveError);
        // Continue anyway - the report was generated successfully
      }

      // Save report to customer's underwriting reports if customer is selected
      if (selectedCustomer) {
        try {
          console.log('💾 Saving comparison report to customer:', selectedCustomer.name);
          const customerSaveResponse = await fetch(
            `${API_BASE_URL}/customers/${selectedCustomer.id}/reports`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                title: reportTitle,
                content: reportContent,
                citations: citations,
                validationResults: validationResults
              })
            }
          );
          
          if (customerSaveResponse.ok) {
            console.log('✅ Comparison report saved to customer successfully:', selectedCustomer.name);
          } else {
            console.error('Failed to save comparison report to customer:', await customerSaveResponse.text());
          }
        } catch (customerSaveError) {
          console.error('Error saving comparison report to customer:', customerSaveError);
          // Continue anyway - the main report was generated successfully
        }
      }

      setProgressStatus({ message: 'Complete!', progress: 100 });
      setComparisonReport(reportContent);
      setReportCitations(citations);

      // Initialize chat with report context
      const initialMessage: ChatMessage = {
        id: `msg_${Date.now()}`,
        content: `I've generated your comparison report analyzing ${documentNames.join(', ')}. The report includes ${citations.length} citations linking back to specific document sections. ${selectedCustomer ? `This report has been automatically saved to ${selectedCustomer.name}'s profile.` : ''} Feel free to ask me any questions about the findings - I can provide citations to support my responses!`,
        sender: 'ai',
        timestamp: new Date(),
        citations: citations
      };

      setChatHistory([initialMessage]);
      setCurrentStep('view-report');

    } catch (error) {
      console.error('Error generating report:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate report');
    }
  };

  const handleSendChatMessage = async (message: string, displayMessage?: string) => {
    if (!comparisonReport) return;

    // When a template is used, render the template title in the chat UI rather
    // than the raw prompt body — keeps template IP out of view.
    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      content: displayMessage || message,
      sender: 'user',
      timestamp: new Date()
    };

    setChatHistory(prev => [...prev, userMessage]);

    // Get ALL documents for context - customer docs + wizard uploaded docs
    const selectedDocuments = [...customerDocuments, ...wizardUploadedDocs];
    
    // Build spans context for selected documents
    const spanBlocks = await Promise.all(
      selectedDocuments.map(async doc => {
        const res = await fetch(`${API_BASE_URL}/api/documents/${doc.id}/spans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const spans = res.ok ? await res.json() : [];
        return spansToPrompt(doc.id, spans);
      })
    );
    const docContext = spanBlocks.join('\n\n');
    // Create comprehensive context with both the report AND the original documents (if available)
    let contextualPrompt = '';
    
    if (selectedDocuments.length > 0) {
      const documentTexts = selectedDocuments.map((doc, index) =>
        `DOCUMENT ${index + 1}: ${doc.name} (Policy Type: ${doc.policyType || 'Unknown'})
${doc.extractedText || 'Unable to extract text from this document.'}`
      ).join('\n\n---\n\n');

      contextualPrompt = `Based on the following comparison report, original documents (with span metadata), and user question, provide a helpful response:

DOCUMENT SPANS:
${docContext}

COMPARISON REPORT:
${comparisonReport}

ORIGINAL DOCUMENTS:
${documentTexts}

USER QUESTION: ${message}

Please provide a detailed and helpful response based on both the comparison report and the original document content.
`;
    } else {
      // Fallback when documents are not available - use report only
      contextualPrompt = `Based on the following comparison report and user question, provide a helpful response (no original documents available):

COMPARISON REPORT:
${comparisonReport}

USER QUESTION: ${message}

Please provide a detailed and helpful response based on the comparison report content.
`;
    }

    try {
      let aiResponse = '';
      let responseCitations: any[] = [];
      
      // Use Smart AI service with citations for comparison-related chat
      const smartResponse = await fetch(`${API_BASE_URL}/api/chat/smart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: contextualPrompt,
          // Persist only the template title (or the raw user input if no template)
          // — never the full template prompt body wrapped in contextualPrompt.
          displayMessage: displayMessage || message,
          documents: selectedDocuments.map(doc => ({
            id: doc.id,
            name: doc.name,
            extractedText: doc.extractedText || '',
            policyType: doc.policyType || undefined,
            effectiveDate: doc.uploadedAt,
            sections: []
          })),
          options: {
            smartCitations: true,
            enhanceCitations: true,
            validateCitations: true
          }
        })
      });
      
      if (smartResponse.ok) {
        const smartResult = await smartResponse.json();
        aiResponse = smartResult.response || smartResult.message || '';
        responseCitations = smartResult.citations || [];
        console.log(`📊 Chat received ${responseCitations.length} citations from Smart AI`);
      } else {
        console.error('🚨 Smart AI chat failed - NO FALLBACK');
        aiResponse = `❌ **Comparison Chat Error**\n\nThe comparison chat service failed. Please:\n1. Check that documents are properly processed\n2. Verify backend connectivity\n3. Try refreshing the comparison report\n\nError: Smart service unavailable`;
        
        // Update AI message with error
        setChatHistory(prev => {
          const withoutLastAI = prev.filter(msg => msg.id !== 'ai_response_temp');
          return [
            ...withoutLastAI,
            {
              id: 'ai_response_temp',
              content: aiResponse,
              sender: 'ai' as const,
              timestamp: new Date()
            }
          ];
        });
      }

      // Finalize AI message with proper ID and citations
      setChatHistory(prev => {
        const withoutTemp = prev.filter(msg => msg.id !== 'ai_response_temp');
        return [
          ...withoutTemp,
          {
            id: `msg_${Date.now()}_ai`,
            content: aiResponse,
            sender: 'ai' as const,
            timestamp: new Date(),
            citations: responseCitations
          }
        ];
      });

    } catch (error) {
      console.error('Chat message failed:', error);
      
      const errorMessage: ChatMessage = {
        id: `msg_${Date.now()}_error`,
        content: 'I apologize, but I encountered an error processing your message. Please try again.',
        sender: 'ai',
        timestamp: new Date()
      };

      setChatHistory(prev => [...prev, errorMessage]);
    }
  };

  const handleClearChatHistory = () => {
    setChatHistory([]);
  };

  const handleExportPDF = async () => {
    try {
      // Parse the comparison report to extract structured data
      let jsonData;
      try {
        jsonData = JSON.parse(comparisonReport);
      } catch (e) {
        console.error('Failed to parse comparison report as JSON:', e);
        alert('This report format is not supported for the new PDF export preview. Please use the legacy export.');
        return;
      }

      // Store the data in sessionStorage for the PdfExportPreview window
      const exportData = {
        data: jsonData,
        reportTitle: reportTitle || 'Comparison Report',
        timestamp: new Date().toISOString(),
        customerName: selectedCustomer?.name || 'Unknown Customer',
        documents: documents.map(doc => ({ id: doc.id, name: doc.name }))
      };

      sessionStorage.setItem('pdf-export-data', JSON.stringify(exportData));

      // Open PdfExportPreview in a new window
      const previewWindow = window.open(
        '/pdf-export-preview',
        '_blank',
        'width=1400,height=900,scrollbars=yes,resizable=yes'
      );

      if (!previewWindow) {
        alert('Please allow pop-ups to use the PDF export preview feature.');
      }
    } catch (error) {
      console.error('Error opening PDF export preview:', error);
      alert('Failed to open PDF export preview. Please try again.');
    }
  };

  const handleExportDOC = () => {
    if (!comparisonReport || !reportContentRef.current) return;

    try {
      // Get the actual rendered HTML content
      const originalContent = reportContentRef.current;
      const clonedContent = originalContent.cloneNode(true) as HTMLElement;
      
      // Clean up styles for Word document
      const allElements = clonedContent.querySelectorAll('*');
      allElements.forEach(el => {
        const element = el as HTMLElement;
        // Remove any classes that might interfere
        element.removeAttribute('class');
        // Set basic styling
        if (element.tagName === 'H1') element.style.cssText = 'color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; margin-top: 20px;';
        if (element.tagName === 'H2') element.style.cssText = 'color: #1e40af; margin-top: 20px; margin-bottom: 10px;';
        if (element.tagName === 'H3') element.style.cssText = 'color: #1e40af; margin-top: 15px; margin-bottom: 8px;';
        if (element.tagName === 'P') element.style.cssText = 'margin-bottom: 10px; line-height: 1.6;';
        if (element.tagName === 'UL' || element.tagName === 'OL') element.style.cssText = 'margin-bottom: 10px; padding-left: 20px;';
        if (element.tagName === 'LI') element.style.cssText = 'margin-bottom: 5px;';
      });

      // Create Word document content
      const docContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Insurance Policy Comparison Report</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; color: #333; }
            h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
            h2 { color: #1e40af; margin-top: 30px; }
            h3 { color: #1e3a8a; margin-top: 20px; }
            p { margin-bottom: 10px; }
            ul, ol { margin-bottom: 10px; padding-left: 20px; }
            li { margin-bottom: 5px; }
            table { border-collapse: collapse; width: 100%; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            code { background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: monospace; }
            pre { background-color: #f5f5f5; padding: 10px; border-radius: 5px; overflow-x: auto; }
            blockquote { border-left: 4px solid #ddd; margin: 16px 0; padding-left: 16px; color: #666; }
          </style>
        </head>
        <body>
          <h1>Insurance Policy Comparison Report</h1>
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <hr>
          ${clonedContent.innerHTML}
        </body>
        </html>
      `;

      const blob = new Blob([docContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comparison-report-${new Date().toISOString().split('T')[0]}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DOC export failed:', error);
      alert('Failed to export Word document. Please try again.');
    }
  };

  const handleCitationClick = async (citation: any) => {
    try {
      // Handle quote-based citations (from [CITE: "..."] format)
      if (citation.type === 'quote' && citation.text) {
        const authToken = localStorage.getItem('riskninja-token');
        if (!authToken) {
          console.error('No auth token available for citation search');
          return;
        }

        // Determine which document to search - use citation's docId if available,
        // otherwise try the selected documents in the comparison
        let searchDocumentId = citation.docId || citation.documentId;
        if (!searchDocumentId && selectedDocumentIds.length > 0) {
          searchDocumentId = selectedDocumentIds[0];
        }
        if (!searchDocumentId && documents.length > 0) {
          searchDocumentId = documents[0].id;
        }

        if (!searchDocumentId) {
          console.error('No document available to search for citation');
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/chat/citations/find-quote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            documentId: searchDocumentId,
            quote: citation.text
          })
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const result = await response.json();

        if (result.found && result.pageNumber) {
          const textForPdfSearch = result.foundText || citation.text;
          setSelectedCitation({
            id: citation.id,
            page: result.pageNumber,
            text: textForPdfSearch,
            startOffset: result.startOffset || 0,
            endOffset: result.endOffset || textForPdfSearch.length,
            documentId: searchDocumentId,
            documentName: result.documentName || 'Document'
          });
          setShowPdfViewer(true);
        } else {
          console.log('Quote not found in document:', result.documentName);
        }
        return;
      }

      // Handle page-based citations (from UniversalJsonRenderer)
      if (citation.sourceDocId || citation.documentId) {
        const docId = citation.sourceDocId || citation.documentId;
        const docName = citation.documentName || documents.find(d => d.id === docId)?.name || 'Document';
        setSelectedCitation({
          id: citation.id,
          page: citation.page || 1,
          text: citation.text || citation.section || '',
          startOffset: 0,
          endOffset: 0,
          documentId: docId,
          documentName: docName
        });
        setShowPdfViewer(true);
      }
    } catch (error) {
      console.error('Citation click failed:', error);
    }
  };

  const renderViewReport = () => {
    console.log('🎨 Rendering view-report case');
    return (
      <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-bg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[800px] max-w-[95vw] mx-auto px-4">
          {/* Left Column - Report (2/3 width) */}
          <div className="lg:col-span-2 bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                <h3 className="font-semibold text-secondary dark:text-dark-text">
                    Document Comparison Report
                </h3>
                  <p className="text-sm text-accent dark:text-dark-muted">
                    Comparing {selectedDocumentIds.length} documents • Generated {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900 min-h-[600px]">
              <div className="w-full bg-white dark:bg-dark-bg shadow-lg rounded-lg p-8">
                <div className="text-base leading-relaxed text-secondary dark:text-dark-text">
                  {error ? (
                    <div className="text-center py-12">
                      <div className="text-red-500 text-6xl mb-4">⚠️</div>
                      <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                        Error Loading Report
                      </h3>
                      <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                      <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Return to Home
                      </button>
                    </div>
                  ) : !comparisonReport ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                      <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-2">
                        Loading Report...
                      </h3>
                      <p className="text-accent dark:text-dark-muted">
                        Please wait while we load your comparison report.
                      </p>
                    </div>
                  ) : (
                    <div ref={reportContentRef} className="overflow-x-scroll markdown-content" style={{ overflowX: 'scroll' }}>
                      {(() => {
                        // Check if the report content is JSON
                        console.log('🔍🔍🔍 COMPARISON REPORT FORMAT CHECK');
                        console.log('🔍 Report length:', comparisonReport.length);
                        console.log('🔍 First 200 chars:', comparisonReport.substring(0, 200));

                        try {
                          const jsonData = JSON.parse(comparisonReport);
                          console.log('✅ Successfully parsed as JSON');
                          console.log('🔍 JSON keys:', Object.keys(jsonData));
                          console.log('🔍 Has responseMetadata?', !!jsonData?.responseMetadata);
                          console.log('🔍 responseMetadata workflowType:', jsonData?.responseMetadata?.workflowType);

                          // If it's valid JSON and has the expected structure, render with UniversalJsonRenderer
                          if (jsonData && typeof jsonData === 'object' && jsonData.responseMetadata) {
                            console.log('✅✅✅ Using UniversalJsonRenderer for JSON report with customPdfExportHandler');
                            return (
                              <UniversalJsonRenderer
                                data={jsonData}
                                onCitationClick={handleCitationClick}
                                documents={documents.map(doc => ({ id: doc.id, name: doc.name }))}
                                customPdfExportHandler={handleExportPDF}
                              />
                            );
                          } else {
                            console.log('❌ JSON detected but no responseMetadata, falling back to markdown');
                            console.log('🔍 JSON structure:', JSON.stringify(jsonData, null, 2).substring(0, 500));
                          }
                        } catch (e) {
                          console.log('❌ Not valid JSON, using markdown');
                          console.log('❌ Parse error:', e instanceof Error ? e.message : 'Unknown error');
                        }
                        
                        // Default to markdown rendering
                        console.log('📝 Using ReactMarkdown for report');
                        return (
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight, rehypeKatex]}
                          >
                            {comparisonReport}
                          </ReactMarkdown>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Comparison Tools */}
          <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col min-h-[800px]">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="font-semibold text-secondary dark:text-dark-text">
                Comparison Tools
              </h3>
            </div>
            <div className="flex-1 p-4 space-y-4">
              {/* Quick Actions */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Quick Actions</h4>
                <div className="space-y-2">
          <button
            onClick={() => {
              // Reset wizard for new comparison
              setCurrentStep('document-selection');
              setSelectedDocumentIds([]);
              setWizardUploadedDocs([]);
              wizardUploadedDocsRef.current = [];
              setAdditionalFacts('');
              setComparisonReport('');
              setReportCitations([]);
              setChatHistory([]);
              setError(null);
              setShowExportMenu(false);
            }}
                    className="w-full px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Start New Comparison
          </button>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Back to Home
                  </button>
                </div>
              </div>

              {/* Document Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Documents Compared</h4>
                <div className="space-y-2">
                  {[...customerDocuments, ...wizardUploadedDocs].map((doc, index) => (
                    <div key={doc.id} className="text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded border">
                      <div className="font-medium text-secondary dark:text-dark-text truncate">
                        {index + 1}. {doc.name}
                      </div>
                      <div className="text-xs text-accent dark:text-dark-muted">
                        {doc.policyType || 'Unknown Policy'} • {(doc.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Citation Information */}
              {reportCitations.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Report Citations</h4>
                  <div className="text-xs text-accent dark:text-dark-muted space-y-2">
                    <p>• Report contains {reportCitations.length} citations</p>
                    <p>• Click citations in the report to view source documents</p>
                    <p>• Citations link to specific document sections</p>
                    <p>• All factual claims are supported by source evidence</p>
                  </div>
                </div>
              )}

              {/* Comparison Insights */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Comparison Insights</h4>
                <div className="text-xs text-accent dark:text-dark-muted space-y-2">
                  <p>• Review coverage differences carefully</p>
                  <p>• Pay attention to policy limits and deductibles</p>
                  <p>• Note any exclusions or special conditions</p>
                  <p>• Compare premium costs and payment terms</p>
                  <p>• Look for additional benefits or riders</p>
                </div>
              </div>

              {/* Chat Interface */}
              <div className="space-y-3 flex-1 flex flex-col">
                <h4 className="text-sm font-medium text-secondary dark:text-dark-text">Ask Questions</h4>
                <div className="flex-1 min-h-[300px] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <ChatInterface
                    messages={chatHistory}
                    onSendMessage={handleSendChatMessage}
                    isLoading={false}
                    onCitationClick={handleCitationClick}
                    documents={documents.map(doc => ({id: doc.id, name: doc.name}))}
                    className="h-full"
                    onClearChat={handleClearChatHistory}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Quote Citation PDF Viewer */}
        {showPdfViewer && selectedCitation && (
          <UnifiedPdfCitationViewer
            documentId={selectedCitation.documentId}
            citation={{
              documentId: selectedCitation.documentId,
              documentName: selectedCitation.documentName || 'Unknown Document',
              page: selectedCitation.page || 1,
              startOffset: selectedCitation.startOffset || 0,
              endOffset: selectedCitation.endOffset || 0,
              text: selectedCitation.text || '',
              context: `Citation from ${selectedCitation.documentName || 'Unknown Document'}, page ${selectedCitation.page || 1}`
            }}
            onClose={() => {
              setShowPdfViewer(false);
              setSelectedCitation(null);
            }}
            useAnnotationViewer={true}
          />
        )}

        {/* HTML PDF Viewer for Citations (legacy) */}
        {showHtmlPdfViewer && htmlViewerCitation && htmlViewerDocumentUrl && (
          <HtmlPdfViewer
            documentUrl={htmlViewerDocumentUrl}
            citation={{
              documentId: htmlViewerCitation.documentId || '',
              documentName: htmlViewerCitation.documentName || 'Unknown Document',
              page: htmlViewerCitation.page || 1,
              startOffset: htmlViewerCitation.startOffset || 0,
              endOffset: htmlViewerCitation.endOffset || 0,
              text: htmlViewerCitation.text || '',
              context: htmlViewerCitation.context
            }}
            allCitations={allCitationsOnPage}
            onClose={() => {
              setShowHtmlPdfViewer(false);
              setHtmlViewerCitation(null);
              setHtmlViewerDocumentUrl('');
              setAllCitationsOnPage([]);
              setClickedCitation(null);
              setHighlight(null);
            }}
          />
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    
    switch (currentStep) {
      case 'customer-selection':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-secondary dark:text-dark-text mb-4">
                Select Customer
              </h2>
              <p className="text-accent dark:text-dark-muted mb-6">
                Choose a customer to compare documents for.
              </p>
            </div>

            {/* Existing Customers */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
                Existing Customers
              </h3>
              
              {customers.length === 0 ? (
                <p className="text-accent dark:text-dark-muted text-center py-8">
                  No customers available. Add a new customer below to get started.
                </p>
              ) : (
                <div className="grid gap-3">
                  {customers.map((customer) => (
                    <label
                      key={customer.id}
                      className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                                             <input
                         type="radio"
                         checked={selectedCustomer?.id === customer.id}
                         onChange={() => handleCustomerSelection(customer)}
                         className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                       />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-secondary dark:text-dark-text">
                            {customer.name}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Customer */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
                Add New Customer
              </h3>
              {/* Add customer input form */}
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedCustomer) {
                    setCurrentStep('document-selection');
                  }
                }}
                disabled={!selectedCustomer}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        );

      case 'document-selection':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-secondary dark:text-dark-text mb-4">
                Select Documents to Compare
              </h2>
              <p className="text-accent dark:text-dark-muted mb-2">
                Choose at least two documents from {selectedCustomer?.name}'s files to compare.
              </p>
              <p className="text-sm text-accent dark:text-dark-muted mb-6">
                Comparing quotes or policies? The Multi-Line Renewal Review handles those with term-aware analysis.
              </p>
            </div>

            {/* Customer Info */}
            {selectedCustomer && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600">
                    <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                  </svg>
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Comparing documents for: {selectedCustomer.name}
                  </span>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Company: {selectedCustomer.company || 'N/A'} • Type: {selectedCustomer.type}
                </p>
              </div>
            )}

            {/* Customer Documents */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
                Customer Documents
              </h3>
              
              {customerDocuments.length === 0 ? (
                <p className="text-accent dark:text-dark-muted text-center py-8">
                  No documents available for this customer. Upload documents below to get started.
                </p>
              ) : (
                <div className="grid gap-3">
                  {customerDocuments.map((doc) => (
                    <label
                      key={doc.id}
                      className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDocumentIds.includes(doc.id)}
                        onChange={() => handleDocumentSelection(doc.id)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-secondary dark:text-dark-text">
                            {doc.name}
                          </span>
                          <span className="text-sm text-accent dark:text-dark-muted">
                            {(doc.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        </div>
                        <div className="text-sm text-accent dark:text-dark-muted">
                          Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* DEBUG: Show wizard state */}
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <h4 className="font-bold">DEBUG INFO:</h4>
              <p>Customer Docs: {customerDocuments.length}</p>
              <p>Wizard Docs: {wizardUploadedDocs.length}</p>
              <p>Context Docs: {documents.length}</p>
              <p>Selected IDs: {selectedDocumentIds.length}</p>
              <h5 className="font-bold mt-2">Context Documents:</h5>
              {documents.map((doc, idx) => (
                <p key={idx} className="text-xs">
                  {idx + 1}. {doc.name} (ID: {doc.id})
                </p>
              ))}
            </div>

            {/* Wizard Uploaded Documents - ALL AUTO-SELECTED */}
            {wizardUploadedDocs.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.21 2.21a.75.75 0 001.137-.073l3.857-5.397z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-800 font-medium">Documents Ready for Analysis</span>
                </div>
                <p className="text-green-700 text-sm mt-1">
                  {wizardUploadedDocs.length} document{wizardUploadedDocs.length !== 1 ? 's' : ''} uploaded and processed successfully.
                </p>
                <p className="text-green-600 text-xs mt-2">
                  All documents are automatically selected for comparison.
                </p>
                <div className="mt-4 space-y-2">
                  {wizardUploadedDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center space-x-2 p-2 bg-green-100 rounded"
                    >
                      <svg className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-green-800 font-medium">
                        {doc.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload New Documents */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
                Upload New Documents
              </h3>
              <FileUploader 
                onFilesUploaded={handleFilesUploaded}
                customerId={selectedCustomer?.id}
                projectId={selectedCustomer?.id} // Using customer ID as project ID for now
              />
            </div>

            {/* Ready State Indicator */}
            {(() => {
              const totalDocs = customerDocuments.length + wizardUploadedDocs.length;
              return totalDocs >= 2 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.21 2.21a.75.75 0 001.137-.073l3.857-5.397z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-800 font-medium">Documents Ready for Analysis</span>
                  </div>
                  <p className="text-green-700 text-sm mt-1">
                    {totalDocs} document{totalDocs !== 1 ? 's' : ''} uploaded and processed successfully.
                  </p>
                  <p className="text-green-600 text-xs mt-2">
                    All uploaded documents are automatically selected. The next step will begin automatically.
                  </p>
                </div>
              );
            })()}

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
              >
                Cancel
              </button>
              <div className="text-sm text-accent dark:text-dark-muted">
                {(() => {
                  const totalDocs = customerDocuments.length + wizardUploadedDocs.length;
                  if (totalDocs === 0) return 'Upload at least 2 documents to proceed';
                  if (totalDocs === 1) return 'Upload 1 more document to proceed';
                  return 'Ready to proceed automatically';
                })()}
              </div>
            </div>
          </div>
        );

      case 'report-details':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-secondary dark:text-dark-text mb-4">
                Report Details
              </h2>
              <p className="text-accent dark:text-dark-muted mb-6">
                Provide a title for your report and any additional context you'd like the comparison to focus on.
              </p>
            </div>

            {/* Report Title */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-3">
                Report Title *
              </label>
              <input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Enter a title for this comparison report..."
                className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-secondary dark:text-dark-text placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
              <p className="text-sm text-accent dark:text-dark-muted mt-2">
                This title will be used to save and identify the report.
              </p>
            </div>

            {/* Additional Context */}
            <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-3">
                Additional Facts and Context
              </label>
              <textarea
                value={additionalFacts}
                onChange={(e) => setAdditionalFacts(e.target.value)}
                placeholder="Enter any specific requirements, business context, coverage priorities, or questions you'd like addressed in the comparison..."
                className="w-full h-40 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg text-secondary dark:text-dark-text placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              <p className="text-sm text-accent dark:text-dark-muted mt-2">
                This information will help generate a more targeted and relevant comparison report.
              </p>
            </div>

            {/* Selected Documents Summary */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-medium text-secondary dark:text-dark-text mb-2">
                Documents to Compare ({customerDocuments.length + wizardUploadedDocs.length})
              </h4>
              <div className="space-y-1">
                {[...customerDocuments, ...wizardUploadedDocs]
                  .map(doc => (
                    <div key={doc.id} className="text-sm text-accent dark:text-dark-muted">
                      • {doc.name}
                    </div>
                  ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleBackToDocuments}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleNextFromReportDetails}
                disabled={!reportTitle.trim()}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Generate Report
              </button>
            </div>
          </div>
        );

      case 'generating-report':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-secondary dark:text-dark-text mb-4">
                Generating Comparison Report
              </h2>
              <p className="text-accent dark:text-dark-muted mb-8">
                Please wait while we analyze your documents and generate a comprehensive comparison report.
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <div className="flex items-center justify-center space-x-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200 mb-1">
                    Generating Comparison Report
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {progressStatus.message || 'Analyzing documents and creating comprehensive comparison...'}
                  </p>
                </div>
              </div>
              <div className="mt-4 bg-green-200 dark:bg-green-800 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-500 ease-out" 
                  style={{ width: `${progressStatus.progress || 70}%` }}
                ></div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="flex justify-center">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 w-full max-w-md">
                  <p className="text-red-700 dark:text-red-400 text-center">{error}</p>
                  <button
                    onClick={() => {
                      setError(null);
                      setCurrentStep('report-details');
                    }}
                    className="mt-3 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'view-report':
        return renderViewReport();

      default:
        return null;
    }
  };

  // Prevent access for non-licensed users
  if (user?.status !== 'active') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-lg font-bold text-secondary dark:text-dark-text mb-2">
            License Required
          </h2>
          <p className="text-accent dark:text-dark-muted">
            Your account is pending activation by an administrator. AI features are disabled.
          </p>
        </div>
      </div>
    );
  }

  console.log('🎨 Rendering Comparison component');
  console.log('🎨 Current step in render:', currentStep);
  console.log('🎨 Comparison report length in render:', comparisonReport.length);
  console.log('🎨 Chat history length in render:', chatHistory.length);

  return (
    <div className={`flex-1 flex flex-col ${currentStep === 'view-report' ? 'w-full' : 'p-6 max-w-7xl mx-auto'} overflow-y-auto`}>
      {/* Progress Indicator - Hide on view-report step */}
      {currentStep !== 'view-report' && (
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {(['customer-selection', 'document-selection', 'report-details', 'generating-report', 'view-report'] as WizardStep[]).map((step, index) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep === step
                        ? 'bg-primary text-white'
                        : (['customer-selection', 'document-selection', 'report-details', 'generating-report', 'view-report'] as WizardStep[]).indexOf(currentStep) > index
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {(['customer-selection', 'document-selection', 'report-details', 'generating-report', 'view-report'] as WizardStep[]).indexOf(currentStep) > index ? '✓' : index + 1}
                  </div>
                  {index < 4 && (
                    <div
                      className={`w-16 h-1 mx-2 ${
                        (['customer-selection', 'document-selection', 'report-details', 'generating-report', 'view-report'] as WizardStep[]).indexOf(currentStep) > index
                          ? 'bg-green-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
                      <div className="flex space-x-20 mt-2">
              <span className="text-sm text-secondary dark:text-dark-text">Select Customer</span>
              <span className="text-sm text-secondary dark:text-dark-text">Select Documents</span>
              <span className="text-sm text-secondary dark:text-dark-text">Report Details</span>
              <span className="text-sm text-secondary dark:text-dark-text">Generate Report</span>
              <span className="text-sm text-secondary dark:text-dark-text">Review & Chat</span>
            </div>
        </div>
      )}

      {/* Step Content */}
      <div className={`flex-1 ${currentStep === 'view-report' ? 'h-full' : ''}`}>
        {renderStepContent()}
      </div>
    </div>
  );
};

export default Comparison;